import React, { useState, useRef, useEffect } from "react";
import cancelIcon from "../../../assets/images/cancel.png";
import { addSecondaryMember } from "../../../services/resident_api";
import { useLockBodyScroll } from "../../../hooks/useLockBodyScroll";
export default function AddMemberModal({
  isOpen,
  onClose,
  accessToken,
  onMemberAdded,
}) {
  const [name, setName] = useState("");
  const [contact, setContact] = useState("");
  const [gmail, setGmail] = useState("");
  const [contactFile, setContactFile] = useState(null);
  const [error, setError] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState("");

  const modalRef = useRef();

  const isValidContact = (input) => /^\d{10}$/.test(input);
  const isValidName = (input) => /^[a-zA-Z\s]*$/.test(input);

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (!isOpen) {
      setName("");
      setContact("");
      setGmail("");
      setContactFile(null);
      setError(false);
      setApiErrorMessage("");
    }
  }, [isOpen]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sendToAPI = async (name, contact, gmail = "") => {
    const cleanedContact = contact.replace(/\D/g, "").slice(-10);
    const payload = {
      name,
      phone_number: cleanedContact,
      email_id: gmail,
    };

    try {
      const res = await addSecondaryMember(payload, accessToken);
      if (res.success) {
        onClose();
        if (onMemberAdded) {
          onMemberAdded(); // ✅ refresh avatar and member count
        }
      }
    } catch (err) {
      const message =
        err?.response?.data?.message || "❌ Failed to add contact.";
      console.error("❌ API Error:", err?.response?.data);
      setApiErrorMessage(message);
    }
  };

  const handleManualSubmit = async () => {
    const isContactOk = isValidContact(contact);
    setError(!isContactOk);
    setApiErrorMessage("");

    if (name && isContactOk) {
      await sendToAPI(name, contact, gmail);
    }
  };

  const handleUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setContactFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;

      try {
        if (file.name.endsWith(".json")) {
          const parsed = JSON.parse(content);
          const first = parsed[0];
          if (first.name && first.contactNumber) {
            setName(first.name);
            setContact(first.contactNumber.replace(/\D/g, "").slice(-10));
            setGmail(first.email || "");
          }
        } else if (file.name.endsWith(".csv")) {
          const lines = content.trim().split("\n");
          const headers = lines[0].split(",");
          const firstRow = lines[1]?.split(",");

          const nameIndex = headers.indexOf("name");
          const contactIndex = headers.indexOf("contact");
          const gmailIndex = headers.indexOf("gmail");

          if (nameIndex !== -1 && contactIndex !== -1) {
            setName(firstRow[nameIndex]?.trim());
            setContact(firstRow[contactIndex]?.replace(/\D/g, "").slice(-10));
            setGmail(gmailIndex !== -1 ? firstRow[gmailIndex]?.trim() : "");
          }
        } else if (file.name.endsWith(".vcf")) {
          const lines = content.split("\n");
          let parsedName = "";
          let parsedContact = "";

          lines.forEach((line) => {
            if (line.startsWith("FN:"))
              parsedName = line.replace("FN:", "").trim();
            if (line.startsWith("TEL")) {
              const match = line.match(/TEL[^:]*:(.+)/);
              if (match) parsedContact = match[1].trim();
            }
          });

          setName(parsedName);
          setContact(parsedContact.replace(/\D/g, "").slice(-10));
        }
      } catch (err) {
        console.error("❌ File parsing error:", err);
      }
    };

    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div
        ref={modalRef}
        className="bg-white rounded-xl p-6 w-[90%] max-w-md shadow-lg space-y-5"
      >
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium">Add Contact No</span>
          <img
            src={cancelIcon}
            alt="Cancel"
            className="w-6 h-6 cursor-pointer"
            onClick={onClose}
          />
        </div>

        {/* Upload Button */}
        {/* <div className="flex flex-col items-center space-y-3">
          <label
            htmlFor="contactFile"
            className="bg-[#2699FF] hover:bg-[#187fd6] text-white py-2 px-6 rounded-md cursor-pointer transition text-center"
          >
            📤 Upload Contact (.vcf / .json / .csv)
          </label>
          <input
            id="contactFile"
            type="file"
            accept=".vcf,.json,.csv"
            className="hidden"
            onChange={handleUpload}
          />
          {contactFile && (
            <p className="text-xs text-gray-600">
              Selected: {contactFile.name}
            </p>
          )}
        </div> */}

        {/* Divider */}
        {/* <div className="flex items-center gap-2">
          <hr className="flex-grow border-gray-300" />
          <span className="text-sm text-gray-500">or</span>
          <hr className="flex-grow border-gray-300" />
        </div> */}

        {/* Inputs */}
        <input
          type="text"
          placeholder="Enter Full Name"
          value={name}
          onChange={(e) => {
            const input = e.target.value;
            if (isValidName(input)) setName(input);
          }}
          className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring focus:border-blue-400"
        />

        <input
          type="text"
          placeholder="Contact No"
          value={contact}
          onChange={(e) => {
            const input = e.target.value.replace(/\D/g, "").slice(-10);
            if (input.length <= 10) {
              setContact(input);
              setError(false);
            }
          }}
          className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring ${
            error ? "border-red-500" : "focus:border-blue-400"
          }`}
          maxLength={10}
        />

        <input
          type="email"
          placeholder="Enter Gmail (optional)"
          value={gmail}
          onChange={(e) => {
            setGmail(e.target.value);
            setApiErrorMessage("");
          }}
          className={`w-full border rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring ${
            apiErrorMessage.includes("email")
              ? "border-red-500"
              : "focus:border-blue-400"
          }`}
        />

        {/* Error Message */}
        {apiErrorMessage && (
          <p className="text-red-600 text-sm text-center">{apiErrorMessage}</p>
        )}

        {/* Submit */}
        <div className="flex justify-center pt-2">
          <button
            onClick={handleManualSubmit}
            className="bg-[#2699FF] text-white w-80 py-2 rounded-lg shadow hover:bg-[#187fd6] transition"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}
